// Route guard for `/platform/*` — task 130 D6 + task 158d.
//
// Reads the `PlatformAdminContext` status and:
//   - `unknown`   → renders nothing (no chrome before the probe resolves;
//                   the "hide not show" pattern in task 130 §D5).
//   - `not_admin` → redirects to `/`.
//   - `admin`     → lazy-loads and renders the requested platform page.
//
// Lives in its own file so `router.tsx` does not mix component and
// non-component exports (keeps the react-refresh lint rule happy and
// isolates the guard logic from the route table).
//
// 158d: extended to accept a `page` prop so the same guard wraps four
// platform pages (tenants, hosts, host-detail, categories) without
// duplicating the auth check.

import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { usePlatformAdminStatus } from '@/context/PlatformAdminContext';

const PlatformTenantsPage = lazy(() =>
  import('@/pages/platform/PlatformTenantsPage').then((m) => ({ default: m.PlatformTenantsPage })),
);
const PlatformHostsPage = lazy(() =>
  import('@/pages/platform/PlatformHostsPage').then((m) => ({ default: m.PlatformHostsPage })),
);
const PlatformHostDetailPage = lazy(() =>
  import('@/pages/platform/PlatformHostDetailPage').then((m) => ({
    default: m.PlatformHostDetailPage,
  })),
);
const PlatformCategoriesPage = lazy(() =>
  import('@/pages/platform/PlatformCategoriesPage').then((m) => ({
    default: m.PlatformCategoriesPage,
  })),
);
const PlatformTaxonomyEditorPage = lazy(() =>
  import('@/pages/platform/PlatformTaxonomyEditorPage').then((m) => ({
    default: m.PlatformTaxonomyEditorPage,
  })),
);
const PlatformPromotionStatusPage = lazy(() =>
  import('@/pages/platform/PlatformPromotionStatusPage').then((m) => ({
    default: m.PlatformPromotionStatusPage,
  })),
);

export type PlatformPage =
  | 'tenants'
  | 'hosts'
  | 'host-detail'
  | 'categories'
  | 'categories-edit'
  | 'promotion-status';

interface PlatformRouteGuardProps {
  page?: PlatformPage;
}

export function PlatformRouteGuard({ page = 'tenants' }: PlatformRouteGuardProps) {
  const status = usePlatformAdminStatus();
  if (status === 'unknown') return null;
  if (status === 'not_admin') return <Navigate to="/" replace />;
  return (
    <Suspense fallback={null}>
      {page === 'tenants' && <PlatformTenantsPage />}
      {page === 'hosts' && <PlatformHostsPage />}
      {page === 'host-detail' && <PlatformHostDetailPage />}
      {page === 'categories' && <PlatformCategoriesPage />}
      {page === 'categories-edit' && <PlatformTaxonomyEditorPage />}
      {page === 'promotion-status' && <PlatformPromotionStatusPage />}
    </Suspense>
  );
}
